import React from 'react';
import { MoreVertical, Music, TrendingUp, DollarSign, Edit, Trash2, Eye } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Artist } from '../../types';

interface ArtistCardProps {
  artist: Artist;
  onEdit: (artist: Artist) => void;
  onDelete: (artistId: string) => void;
  onViewDetails: (artist: Artist) => void;
}

export const ArtistCard: React.FC<ArtistCardProps> = ({
  artist,
  onEdit,
  onDelete,
  onViewDetails
}) => {
  const [showMenu, setShowMenu] = React.useState(false);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <Card className="relative group" hover>
      {/* Artist Image */}
      <div className="relative mb-4">
        <img
          src={artist.image}
          alt={artist.name}
          className="w-full h-48 object-cover rounded-lg"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
          <Button
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => onViewDetails(artist)}
          >
            View Details
          </Button>
        </div>
        
        {/* Action Menu */}
        <div className="absolute top-2 right-2">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-10 bg-black-800 border border-gray-700 rounded-lg shadow-lg z-10 min-w-40">
              <button
                onClick={() => {
                  onViewDetails(artist);
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center space-x-2"
              >
                <Eye className="w-4 h-4" />
                <span>View Details</span>
              </button>
              <button
                onClick={() => {
                  onEdit(artist);
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <hr className="border-gray-700" />
              <button
                onClick={() => {
                  onDelete(artist.id);
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 text-red-400 flex items-center space-x-2"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Artist Info */}
      <div className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-white font-display">
            {artist.name}
          </h3>
          <p className="text-sm text-gray-400 line-clamp-2 mt-1">
            {artist.bio}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-700">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Music className="w-4 h-4 text-blue-400 mr-1" />
              <span className="text-lg font-bold text-white">
                {artist.totalSongs}
              </span>
            </div>
            <span className="text-xs text-gray-400">Songs</span>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
              <span className="text-lg font-bold text-white">
                {formatNumber(artist.totalStreams)}
              </span>
            </div>
            <span className="text-xs text-gray-400">Streams</span>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <DollarSign className="w-4 h-4 text-gold-400 mr-1" />
              <span className="text-lg font-bold text-white">
                ${artist.totalRevenue.toLocaleString()}
              </span>
            </div>
            <span className="text-xs text-gray-400">Revenue</span>
          </div>
        </div>

        {/* Social Links */}
        <div className="flex space-x-2 pt-2">
          {artist.socialLinks.instagram && (
            <a
              href={`https://instagram.com/${artist.socialLinks.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-sm hover:scale-105 transition-transform"
            >
              📷
            </a>
          )}
          {artist.socialLinks.spotify && (
            <a
              href={`https://open.spotify.com/artist/${artist.socialLinks.spotify}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white text-sm hover:scale-105 transition-transform"
            >
              🎵
            </a>
          )}
          {artist.socialLinks.youtube && (
            <a
              href={`https://youtube.com/c/${artist.socialLinks.youtube}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center text-white text-sm hover:scale-105 transition-transform"
            >
              📺
            </a>
          )}
          {artist.socialLinks.twitter && (
            <a
              href={`https://twitter.com/${artist.socialLinks.twitter}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white text-sm hover:scale-105 transition-transform"
            >
              🐦
            </a>
          )}
        </div>
      </div>
    </Card>
  );
};